import { QueryFunctionContext, useInfiniteQuery, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import {
  LimitedSchemaInfiniteQueryOptions,
  LimitedSchemaFetchInfiniteQueryOptions,
} from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetAppointmentsTableViewIO } from '../types';

export const getGetAppointmentsTableViewQueryKey = (req: GetAppointmentsTableViewIO['input']) =>
  ['AnalyticsGatewayV1', 'GetAppointmentsTableView', req] as const;
type QueryKey = ReturnType<typeof getGetAppointmentsTableViewQueryKey>;
type QueryFnArgs = QueryFunctionContext<QueryKey, GetAppointmentsTableViewPageParams>;

type GetAppointmentsTableViewPageParams = {
  pageToken?: string;
  pageSize?: number;
};
/**
 * A hook that returns an infinite query for the `GetAppointmentsTableView` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentsTableViewQuery = <E = unknown, D = GetAppointmentsTableViewIO['output']>(
  req: GetAppointmentsTableViewIO['input'],
  options?: Omit<
    LimitedSchemaInfiniteQueryOptions<GetAppointmentsTableViewIO, E, D, QueryKey>,
    'getNextPageParam' | 'getPreviousPageParam'
  >,
  httpOptions?: Options
) =>
  useInfiniteQuery<GetAppointmentsTableViewIO['output'], E, D, QueryKey>({
    queryKey: getGetAppointmentsTableViewQueryKey(req),
    queryFn: ({ pageParam }: QueryFnArgs) => {
      return SchemaAnalyticsGateway1Service.GetAppointmentsTableView(
        {
          ...req,
          pageToken: pageParam?.pageToken ?? req.pageToken,
          pageSize: pageParam?.pageSize ?? req.pageSize,
        },
        httpOptions
      );
    },
    ...options,
    getNextPageParam: (lastPage): GetAppointmentsTableViewPageParams | undefined => {
      if (lastPage.nextPageToken) {
        return { pageToken: lastPage.nextPageToken };
      }
      return undefined;
    },
  });

/**
 * A hook that returns a function to fetch the query for the `GetAppointmentsTableView` endpoint.
 * It uses `queryClient.fetchInfiniteQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useInfiniteQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetAppointmentsTableView = () => {
  const queryClient = useQueryClient();

  const fetchGetAppointmentsTableView = async <E = unknown, D = GetAppointmentsTableViewIO['output']>(
    req: GetAppointmentsTableViewIO['input'],
    options?: LimitedSchemaFetchInfiniteQueryOptions<GetAppointmentsTableViewIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchInfiniteQuery(
      getGetAppointmentsTableViewQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetAppointmentsTableView(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetAppointmentsTableView;
};
